import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { okJson } from '../services/httpError';
import {
  Plus,
  Search,
  X,
  Clock,
  Loader2,
  RefreshCw,
  Building2,
  Edit2,
  Calendar,
  Phone,
  Mail,
  User,
  Users,
  Trash2,
  ChevronRight,
  ChevronDown,
  Briefcase,
  Shield,
  CheckCircle2,
} from 'lucide-react';
import { useCustomer } from '../contexts/CustomerContext';
import { useKeycloak } from '../contexts/KeycloakContext';
import { condoApi, fetchWithAuth, makeAbsolutePhotoUrl } from '../services/api';
import toast from 'react-hot-toast';
import { cn } from '../utils/cn';
import { usePersonDetailContext } from '../contexts/PersonDetailContext';
import VisitorRegistrationModal from '../components/VisitorRegistrationModal';
import { useConfirm } from '../components/ui/ConfirmDialog';

// ─── Types ────────────────────────────────────────────────────────────────────

type PageView = 'list' | 'schedule' | 'contractors';

export interface ServiceProviderItem {
  person_id: string;
  role_id: string;
  name: string;
  photo: string;
  cpf?: string;
  phone?: string;
  email?: string;
  valid_from: string;
  valid_until?: string;
  role_metadata: Record<string, any>;
  status_today: string;
  last_entry_at?: string;
  is_authorized?: boolean;
}

interface UnitOption {
  id: string;
  number: string;
  block?: { name: string };
}

export interface WindowEditState {
  personId: string;
  name: string;
  windowStart: string;
  windowEnd: string;
  allowedDays: number[];
  contractorId?: string;
  contractorName?: string;
}

interface Contractor {
  id: string;
  name: string;
  responsible?: string;
  phone?: string;
  email?: string;
  notes?: string;
  is_active: boolean;
  created_at: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const DAY_LABELS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

function parseAllowedDays(meta: Record<string, any>): number[] {
  const raw = meta?.allowed_days;
  if (!raw) return [1, 2, 3, 4, 5];
  if (typeof raw === 'string') {
    try { return JSON.parse(raw) as number[]; } catch { return [1, 2, 3, 4, 5]; }
  }
  if (Array.isArray(raw)) return raw.map(Number);
  return [1, 2, 3, 4, 5];
}

function formatDays(days: number[]): string {
  if (days.length === 7) return 'Todos os dias';
  if (JSON.stringify([...days].sort()) === JSON.stringify([1, 2, 3, 4, 5])) return 'Seg–Sex';
  if (JSON.stringify([...days].sort()) === JSON.stringify([1, 2, 3, 4, 5, 6])) return 'Seg–Sáb';
  return days.map(d => DAY_LABELS[d]).join(', ');
}

function formatRelativeTime(dateStr?: string): string {
  if (!dateStr) return '—';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'agora';
  if (diffMins < 60) return `${diffMins}min atrás`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h atrás`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d atrás`;
  return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
}

function StatusBadge({ status }: { status: string }) {
  switch (status) {
    case 'inside':
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Dentro</span>;
    case 'awaiting_entry':
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">Aguardando</span>;
    case 'expired':
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">Não entrou</span>;
    case 'exited':
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">Saiu</span>;
    default:
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">—</span>;
  }
}

// ─── Window Edit Modal ────────────────────────────────────────────────────────

export function WindowEditModal({
  state,
  customerId,
  onClose,
  onSave,
}: {
  state: WindowEditState;
  customerId: string;
  onClose: () => void;
  onSave: (personId: string, windowStart: string, windowEnd: string, allowedDays: number[], contractorId: string | null, contractorName: string | null) => Promise<void>;
}) {
  const [windowStart, setWindowStart] = useState(state.windowStart);
  const [windowEnd, setWindowEnd] = useState(state.windowEnd);
  const [allowedDays, setAllowedDays] = useState<number[]>(state.allowedDays);
  const [contractorId, setContractorId] = useState<string>(state.contractorId ?? '');
  const [contractors, setContractors] = useState<Contractor[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    condoApi.listContractors(customerId)
      .then(r => setContractors(r.data?.data ?? []))
      .catch(() => {});
  }, [customerId]);

  const toggleDay = (day: number) => {
    setAllowedDays(prev =>
      prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day].sort()
    );
  };

  const handleSave = async () => {
    if (!windowStart || !windowEnd) { toast.error('Preencha o horário de início e fim'); return; }
    if (allowedDays.length === 0) { toast.error('Selecione ao menos um dia'); return; }
    setSaving(true);
    const selectedContractor = contractors.find(c => c.id === contractorId) ?? null;
    try {
      await onSave(state.personId, windowStart, windowEnd, allowedDays, selectedContractor?.id ?? null, selectedContractor?.name ?? null);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6 border border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-base font-semibold text-gray-900">Janela de Acesso</h2>
            <p className="text-sm text-gray-500 mt-0.5">{state.name}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4">
          <div className="flex gap-3">
            <div className="flex-1">
              <label className="block text-xs font-medium text-gray-700 mb-1">Entrada a partir de</label>
              <input type="time" value={windowStart} onChange={e => setWindowStart(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-900 focus:outline-none focus:ring-1 focus:ring-black focus:border-black" />
            </div>
            <div className="flex-1">
              <label className="block text-xs font-medium text-gray-700 mb-1">Entrada até</label>
              <input type="time" value={windowEnd} onChange={e => setWindowEnd(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-900 focus:outline-none focus:ring-1 focus:ring-black focus:border-black" />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-2">Dias permitidos</label>
            <div className="flex gap-1.5 flex-wrap">
              {DAY_LABELS.map((label, idx) => (
                <button key={idx} type="button" onClick={() => toggleDay(idx)}
                  className={cn(
                    'px-2.5 py-1 rounded text-xs font-medium border transition-colors',
                    allowedDays.includes(idx)
                      ? 'bg-black text-white border-black'
                      : 'bg-white border-gray-300 text-gray-600 hover:border-gray-400'
                  )}>
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Empresa contratada</label>
            <select value={contractorId} onChange={e => setContractorId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-900 focus:outline-none focus:ring-1 focus:ring-black focus:border-black">
              <option value="">— Nenhuma —</option>
              {contractors.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
        </div>

        <div className="flex gap-3 mt-5">
          <button onClick={onClose} className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md text-sm font-medium hover:bg-gray-50 transition-colors">
            Cancelar
          </button>
          <button onClick={handleSave} disabled={saving}
            className="flex-1 px-4 py-2 bg-black hover:bg-gray-800 disabled:opacity-50 text-white rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-2">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            Salvar
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Contractor Modal ─────────────────────────────────────────────────────────

function ContractorModal({
  initial,
  onClose,
  onSave,
}: {
  initial?: Contractor | null;
  onClose: () => void;
  onSave: (data: { name: string; responsible: string; phone: string; email: string; notes: string }) => Promise<void>;
}) {
  const [name, setName] = useState(initial?.name ?? '');
  const [responsible, setResponsible] = useState(initial?.responsible ?? '');
  const [phone, setPhone] = useState(initial?.phone ?? '');
  const [email, setEmail] = useState(initial?.email ?? '');
  const [notes, setNotes] = useState(initial?.notes ?? '');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!name.trim()) { toast.error('Informe o nome da empresa'); return; }
    setSaving(true);
    try {
      await onSave({ name: name.trim(), responsible: responsible.trim(), phone: phone.trim(), email: email.trim(), notes: notes.trim() });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const fieldClass = "w-full px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-900 focus:outline-none focus:ring-1 focus:ring-black focus:border-black placeholder-gray-400";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 p-6 border border-gray-200">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-base font-semibold text-gray-900">
            {initial ? 'Editar Empresa' : 'Nova Empresa'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Nome da empresa *</label>
            <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="ABC Serviços Ltda" className={fieldClass} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Responsável</label>
            <input type="text" value={responsible} onChange={e => setResponsible(e.target.value)} placeholder="Carlos Souza" className={fieldClass} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Telefone</label>
              <input type="text" value={phone} onChange={e => setPhone(e.target.value)} placeholder="(11) 99999-9999" className={fieldClass} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">E-mail</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="contato@empresa.com" className={fieldClass} />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Observações</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} placeholder="Informações adicionais..." className={`${fieldClass} resize-none`} />
          </div>
        </div>

        <div className="flex gap-3 mt-5">
          <button onClick={onClose} className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md text-sm font-medium hover:bg-gray-50 transition-colors">
            Cancelar
          </button>
          <button onClick={handleSave} disabled={saving}
            className="flex-1 px-4 py-2 bg-black hover:bg-gray-800 disabled:opacity-50 text-white rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-2">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            Salvar
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Schedule View ────────────────────────────────────────────────────────────

export function ScheduleView({
  items,
  onOpenPerson,
}: {
  items: ServiceProviderItem[];
  onOpenPerson: (personId: string) => void;
}) {
  const today = new Date().getDay();

  const scheduleByDay = useMemo(() => {
    const result: Record<number, { windowKey: string; sps: ServiceProviderItem[] }[]> = {};
    for (let d = 0; d < 7; d++) {
      const daySps = items.filter(sp => parseAllowedDays(sp.role_metadata).includes(d));
      const windowGroups: Record<string, ServiceProviderItem[]> = {};
      for (const sp of daySps) {
        const isAlways = sp.role_metadata?.no_window_restriction === true;
        const ws = sp.role_metadata?.entry_window_start ?? '??:??';
        const we = sp.role_metadata?.entry_window_end ?? '??:??';
        const key = isAlways ? 'Sempre' : `${ws}–${we}`;
        if (!windowGroups[key]) windowGroups[key] = [];
        windowGroups[key].push(sp);
      }
      result[d] = Object.entries(windowGroups)
        .sort((a, b) => a[0].localeCompare(b[0]))
        .map(([windowKey, sps]) => ({ windowKey, sps }));
    }
    return result;
  }, [items]);

  if (items.length === 0) {
    return (
      <div className="text-center py-12">
        <Calendar className="mx-auto h-12 w-12 text-gray-400" />
        <p className="mt-2 text-sm text-gray-500">Nenhum prestador cadastrado</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <div className="grid grid-cols-7 gap-px bg-gray-200 rounded-lg overflow-hidden min-w-[700px]">
        {DAY_LABELS.map((label, d) => {
          const count = scheduleByDay[d]?.reduce((sum, g) => sum + g.sps.length, 0) ?? 0;
          const isToday = d === today;
          return (
            <div key={d} className={cn('px-3 py-2 text-center', isToday ? 'bg-gray-900' : 'bg-gray-50')}>
              <div className={cn('text-xs font-semibold uppercase tracking-wider', isToday ? 'text-white' : 'text-gray-500')}>
                {label}
              </div>
              {count > 0 && (
                <div className={cn('text-xs mt-0.5', isToday ? 'text-gray-300' : 'text-gray-400')}>
                  {count}
                </div>
              )}
            </div>
          );
        })}

        {DAY_LABELS.map((_, d) => {
          const groups = scheduleByDay[d] ?? [];
          const isToday = d === today;
          return (
            <div key={d} className={cn('min-h-[120px] p-2 space-y-2', isToday ? 'bg-gray-50' : 'bg-white')}>
              {groups.length === 0 ? (
                <p className="text-center text-xs text-gray-300 pt-4">—</p>
              ) : (
                groups.map(({ windowKey, sps }) => (
                  <div key={windowKey} className="space-y-1">
                    <div className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 text-center">
                      {windowKey}
                    </div>
                    {sps.map(sp => (
                      <button key={sp.person_id} onClick={() => onOpenPerson(sp.person_id)}
                        className="w-full text-left px-1.5 py-1 rounded hover:bg-gray-100 transition-colors">
                        <span className="text-xs text-gray-700 leading-tight block truncate">
                          {sp.name.split(' ').slice(0, 2).join(' ')}
                        </span>
                        {sp.role_metadata?.contractor_name && (
                          <span className="text-[10px] text-gray-400 truncate block">
                            {sp.role_metadata.contractor_name}
                          </span>
                        )}
                        {isToday && <StatusBadge status={sp.status_today} />}
                      </button>
                    ))}
                  </div>
                ))
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Contractors View ─────────────────────────────────────────────────────────

// Parse role_metadata safely — handles both object and JSON-string forms
function parseMeta(raw: Record<string, any> | string | null | undefined): Record<string, any> {
  if (!raw) return {};
  if (typeof raw === 'string') { try { return JSON.parse(raw); } catch { return {}; } }
  return raw;
}

export function ContractorsView({ customerId }: { customerId: string }) {
  const [contractors, setContractors] = useState<Contractor[]>([]);
  const [serviceProviders, setServiceProviders] = useState<ServiceProviderItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Contractor | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const { openPersonDetail } = usePersonDetailContext();
  const { selectedCustomer } = useCustomer();
  const { hasRole } = useKeycloak();
  const confirm = useConfirm();

  const canManageContractors =
    hasRole('admin') || hasRole('super_admin') || hasRole('supervisor') || hasRole('sindico') ||
    (hasRole('porteiro') && !!(selectedCustomer as any)?.porteiro_can_manage_contractors);

  const fetchAll = useCallback(async () => {
    setIsLoading(true);
    try {
      const [contractorsRes, spRes] = await Promise.all([
        condoApi.listContractors(customerId),
        condoApi.getServiceProviders(customerId, {}),
      ]);
      setContractors(contractorsRes.data?.data ?? []);
      setServiceProviders(spRes.data?.items ?? []);
    } catch {
      toast.error('Erro ao carregar dados');
    } finally {
      setIsLoading(false);
    }
  }, [customerId]);

  const fetchContractors = useCallback(async () => {
    try {
      const res = await condoApi.listContractors(customerId);
      setContractors(res.data?.data ?? []);
    } catch {
      toast.error('Erro ao carregar empresas');
    }
  }, [customerId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleSave = async (data: { name: string; responsible: string; phone: string; email: string; notes: string }) => {
    if (editing) {
      await condoApi.updateContractor(customerId, editing.id, data);
      toast.success('Empresa atualizada');
    } else {
      await condoApi.createContractor(customerId, data);
      toast.success('Empresa cadastrada');
    }
    fetchAll();
  };

  const handleDelete = async (c: Contractor) => {
    if (!await confirm({ message: `Remover "${c.name}"?`, variant: 'danger', confirmLabel: 'Remover' })) return;
    try {
      await condoApi.deleteContractor(customerId, c.id);
      toast.success('Empresa removida');
      fetchAll();
    } catch {
      toast.error('Erro ao remover empresa');
    }
  };

  const cardAccents = [
    { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-200' },
    { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-200' },
    { bg: 'bg-emerald-100', text: 'text-emerald-700', border: 'border-emerald-200' },
    { bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-200' },
    { bg: 'bg-rose-100', text: 'text-rose-700', border: 'border-rose-200' },
    { bg: 'bg-cyan-100', text: 'text-cyan-700', border: 'border-cyan-200' },
    { bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-200' },
    { bg: 'bg-indigo-100', text: 'text-indigo-700', border: 'border-indigo-200' },
  ];

  const unassigned = serviceProviders.filter(sp => !parseMeta(sp.role_metadata).contractor_id);
  const assignedCount = serviceProviders.length - unassigned.length;

  return (
    <div className="flex-1 overflow-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-base font-semibold text-gray-900">Empresas Contratadas</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            {contractors.length > 0
              ? `${contractors.length} empresa${contractors.length !== 1 ? 's' : ''} · ${assignedCount} prestador${assignedCount !== 1 ? 'es' : ''} vinculado${assignedCount !== 1 ? 's' : ''}`
              : 'Agrupe prestadores por empresa contratada'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchAll}
            disabled={isLoading}
            className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            title="Atualizar"
          >
            <RefreshCw className={cn('h-4 w-4', isLoading && 'animate-spin')} />
          </button>
          {canManageContractors && (
            <button
              onClick={() => { setEditing(null); setModalOpen(true); }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-black hover:bg-gray-800 text-white rounded-lg text-sm font-medium transition-colors shadow-sm"
            >
              <Plus className="h-4 w-4" />
              Nova Empresa
            </button>
          )}
        </div>
      </div>

      {isLoading && contractors.length === 0 ? (
        <div className="flex justify-center items-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black" />
        </div>
      ) : contractors.length === 0 && unassigned.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div className="h-16 w-16 rounded-2xl bg-gray-100 flex items-center justify-center mb-4">
            <Building2 className="h-8 w-8 text-gray-400" />
          </div>
          <h3 className="text-sm font-semibold text-gray-900 mb-1">Nenhuma empresa cadastrada</h3>
          <p className="text-sm text-gray-500 max-w-xs">
            Crie empresas para organizar e agrupar os prestadores de serviço.
          </p>
          {canManageContractors && (
            <button
              onClick={() => { setEditing(null); setModalOpen(true); }}
              className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-black text-white rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors"
            >
              <Plus className="h-4 w-4" />
              Criar primeira empresa
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {contractors.map((c, idx) => {
            const isExpanded = expandedId === c.id;
            const members = serviceProviders.filter(sp => {
              const meta = parseMeta(sp.role_metadata);
              return meta.contractor_id === c.id ||
                (!meta.contractor_id && meta.contractor_name === c.name);
            });
            const accent = cardAccents[idx % cardAccents.length];

            return (
              <div key={c.id} className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden hover:shadow-md transition-shadow">
                {/* Card header row */}
                <div
                  className="flex items-center gap-4 p-4 cursor-pointer select-none"
                  onClick={() => setExpandedId(isExpanded ? null : c.id)}
                >
                  {/* Icon */}
                  <div className={cn('h-11 w-11 rounded-xl flex items-center justify-center flex-shrink-0', accent.bg, accent.text)}>
                    <Building2 className="h-5 w-5" />
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-sm font-semibold text-gray-900">{c.name}</h3>
                      {members.length > 0 && (
                        <span className={cn('inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border', accent.bg, accent.text, accent.border)}>
                          <Users className="h-3 w-3" />
                          {members.length} prestador{members.length !== 1 ? 'es' : ''}
                        </span>
                      )}
                      {members.length === 0 && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-400 border border-gray-200">
                          Sem prestadores
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-4 mt-1.5 flex-wrap">
                      {c.responsible && (
                        <span className="flex items-center gap-1 text-xs text-gray-500">
                          <User className="h-3 w-3" />
                          {c.responsible}
                        </span>
                      )}
                      {c.phone && (
                        <span className="flex items-center gap-1 text-xs text-gray-500">
                          <Phone className="h-3 w-3" />
                          {c.phone}
                        </span>
                      )}
                      {c.email && (
                        <span className="flex items-center gap-1 text-xs text-gray-500">
                          <Mail className="h-3 w-3" />
                          {c.email}
                        </span>
                      )}
                      {c.notes && (
                        <span className="text-xs text-gray-400 truncate max-w-[200px]">{c.notes}</span>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  {canManageContractors && (
                    <div className="flex items-center gap-1 flex-shrink-0" onClick={e => e.stopPropagation()}>
                      <button
                        onClick={() => { setEditing(c); setModalOpen(true); }}
                        className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                        title="Editar"
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(c)}
                        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Remover"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                  <div className="ml-1 text-gray-400 flex-shrink-0">
                    {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  </div>
                </div>

                {/* Expanded members */}
                {isExpanded && (
                  <div className="border-t border-gray-100 bg-gray-50 px-4 py-3">
                    {members.length === 0 ? (
                      <p className="text-xs text-gray-400 italic py-1">Nenhum prestador vinculado a esta empresa</p>
                    ) : (
                      <div className="space-y-0.5">
                        {members.map(sp => (
                          <button
                            key={sp.person_id}
                            onClick={() => openPersonDetail(sp.person_id)}
                            className="flex items-center gap-3 w-full text-left hover:bg-white rounded-lg px-3 py-2 transition-colors group"
                          >
                            <div className="h-10 w-8 rounded-md bg-gray-200 flex items-center justify-center flex-shrink-0 overflow-hidden ring-2 ring-white">
                              {sp.photo
                                ? <img src={makeAbsolutePhotoUrl(sp.photo)} alt={sp.name} className="w-full h-full object-cover" />
                                : <User className="h-4 w-4 text-gray-400" />}
                            </div>
                            <span className="text-sm font-medium text-gray-700 flex-1 group-hover:text-gray-900 truncate">{sp.name}</span>
                            {(() => { const m = parseMeta(sp.role_metadata); return (m.entry_window_start || m.entry_window_end) && (
                              <span className="text-xs text-gray-400 font-mono bg-gray-100 group-hover:bg-white px-1.5 py-0.5 rounded border border-gray-200 transition-colors">
                                {m.entry_window_start}–{m.entry_window_end}
                              </span>
                            ); })()}
                            <StatusBadge status={sp.status_today} />
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {/* Unassigned group */}
          {unassigned.length > 0 && (
            <div className="bg-white rounded-xl border border-dashed border-gray-300 overflow-hidden">
              <div
                className="flex items-center gap-4 p-4 cursor-pointer select-none hover:bg-gray-50 transition-colors"
                onClick={() => setExpandedId(expandedId === '__unassigned__' ? null : '__unassigned__')}
              >
                <div className="h-11 w-11 rounded-xl bg-gray-100 flex items-center justify-center flex-shrink-0">
                  <User className="h-5 w-5 text-gray-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-medium text-gray-500 italic">Sem empresa atribuída</h3>
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
                      <Users className="h-3 w-3" />
                      {unassigned.length} prestador{unassigned.length !== 1 ? 'es' : ''}
                    </span>
                  </div>
                  <p className="text-xs text-gray-400 mt-0.5">Edite o acesso de cada prestador para vincular uma empresa</p>
                </div>
                <div className="text-gray-400">
                  {expandedId === '__unassigned__' ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                </div>
              </div>

              {expandedId === '__unassigned__' && (
                <div className="border-t border-gray-100 bg-gray-50 px-4 py-3">
                  <div className="space-y-0.5">
                    {unassigned.map(sp => (
                      <button
                        key={sp.person_id}
                        onClick={() => openPersonDetail(sp.person_id)}
                        className="flex items-center gap-3 w-full text-left hover:bg-white rounded-lg px-3 py-2 transition-colors group"
                      >
                        <div className="h-10 w-8 rounded-md bg-gray-200 flex items-center justify-center flex-shrink-0 overflow-hidden ring-2 ring-white">
                          {sp.photo
                            ? <img src={makeAbsolutePhotoUrl(sp.photo)} alt={sp.name} className="w-full h-full object-cover" />
                            : <User className="h-4 w-4 text-gray-400" />}
                        </div>
                        <span className="text-sm font-medium text-gray-700 flex-1 group-hover:text-gray-900 truncate">{sp.name}</span>
                        <StatusBadge status={sp.status_today} />
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {modalOpen && (
        <ContractorModal
          initial={editing}
          onClose={() => { setModalOpen(false); setEditing(null); }}
          onSave={handleSave}
        />
      )}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function ServiceProviders() {
  const { selectedCustomer } = useCustomer();
  const { openPersonDetail } = usePersonDetailContext();
  const confirm = useConfirm();

  const [view, setView] = useState<PageView>('list');
  const [items, setItems] = useState<ServiceProviderItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [authStatusFilter, setAuthStatusFilter] = useState<'' | 'authorized' | 'pending'>('');
  const [unitFilter, setUnitFilter] = useState('');
  const [units, setUnits] = useState<UnitOption[]>([]);
  const [windowEditState, setWindowEditState] = useState<WindowEditState | null>(null);
  const [showRegModal, setShowRegModal] = useState(false);
  const [serviceTypeFilter, setServiceTypeFilter] = useState('');

  const customerId = selectedCustomer?.id;

  useEffect(() => {
    if (!customerId) return;
    fetchWithAuth(`/api/v1/customers/${customerId}/units/search?limit=200`)
      .then(r => okJson<any>(r, 'Erro ao carregar as unidades'))
      .then(data => setUnits(data?.units ?? data ?? []))
      .catch((err: any) => toast.error(err?.message || 'Erro ao carregar as unidades'));
  }, [customerId]);

  const fetchServiceProviders = useCallback(async () => {
    if (!customerId) return;
    setIsLoading(true);
    try {
      const res = await condoApi.getServiceProviders(customerId, {
        status: statusFilter !== 'all' ? statusFilter : undefined,
        search: search || undefined,
        unit_id: unitFilter || undefined,
      });
      setItems(res.data?.items ?? []);
    } catch {
      toast.error('Erro ao carregar prestadores');
    } finally {
      setIsLoading(false);
    }
  }, [customerId, statusFilter, search, unitFilter]);

  useEffect(() => {
    if (view !== 'contractors') fetchServiceProviders();
  }, [fetchServiceProviders, view]);

  const fetchAll = useCallback(async () => {
    if (!customerId) return;
    try {
      const res = await condoApi.getServiceProviders(customerId, {});
      setItems(res.data?.items ?? []);
    } catch { /* silent */ }
  }, [customerId]);

  useEffect(() => {
    if (view === 'schedule' || view === 'contractors') fetchAll();
  }, [view, fetchAll]);

  const handleWindowSave = async (
    personId: string,
    windowStart: string,
    windowEnd: string,
    allowedDays: number[],
    contractorId: string | null,
    contractorName: string | null,
  ) => {
    if (!customerId) return;
    try {
      await condoApi.updateServiceProviderWindow(customerId, personId, {
        entry_window_start: windowStart,
        entry_window_end: windowEnd,
        allowed_days: JSON.stringify(allowedDays),
        contractor_id: contractorId,
        contractor_name: contractorName,
      });
      toast.success('Janela de acesso atualizada');
      fetchServiceProviders();
    } catch (err) {
      toast.error('Erro ao atualizar janela');
      throw err;
    }
  };

  const filteredItems = items.filter(item => {
    if (search.trim() && !item.name.toLowerCase().includes(search.toLowerCase())) return false;
    if (statusFilter !== 'all' && item.status_today !== statusFilter) return false;
    if (serviceTypeFilter && item.role_metadata?.service_type !== serviceTypeFilter) return false;
    if (authStatusFilter === 'authorized') {
      const isAuth = item.is_authorized ?? item.role_metadata?.is_authorized;
      if (!isAuth) return false;
    } else if (authStatusFilter === 'pending') {
      const isAuth = item.is_authorized ?? item.role_metadata?.is_authorized;
      if (isAuth) return false;
    }
    return true;
  });

  // Collect unique service types from loaded items for the filter dropdown
  const serviceTypes = Array.from(new Set(
    items.map(item => item.role_metadata?.service_type).filter(Boolean) as string[]
  )).sort();

  if (!customerId) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <Briefcase className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">Selecione um cliente para ver os prestadores</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 h-full flex flex-col">
      {/* Page title */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-xl bg-gray-900 flex items-center justify-center">
              <Briefcase className="h-4 w-4 text-white" />
            </div>
            <div>
              <h1 className="text-base font-semibold text-gray-900">Prestadores de Serviço</h1>
              {items.length > 0 && (
                <p className="text-xs text-gray-500">{items.length} prestador{items.length !== 1 ? 'es' : ''} cadastrado{items.length !== 1 ? 's' : ''}</p>
              )}
            </div>
          </div>
          {view === 'list' && (
            <button
              onClick={() => setShowRegModal(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-black text-white text-sm font-medium rounded-lg hover:bg-gray-800 shadow-sm transition-all"
            >
              <Plus className="h-4 w-4" />
              Novo Prestador
            </button>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b border-gray-200 pb-0">
          {([
            { key: 'list' as PageView, label: 'Lista', icon: Users },
            { key: 'schedule' as PageView, label: 'Agenda', icon: Calendar },
            { key: 'contractors' as PageView, label: 'Empresas', icon: Building2 },
          ]).map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setView(key)}
              className={cn(
                'flex items-center gap-1.5 px-3 py-2 text-sm font-medium whitespace-nowrap border-b-2 -mb-px transition-colors',
                view === key
                  ? 'text-gray-900 border-gray-900'
                  : 'text-gray-500 border-transparent hover:text-gray-700 hover:border-gray-300'
              )}
            >
              <Icon className="h-3.5 w-3.5" />
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* ── LIST VIEW ── */}
      {view === 'list' && (
        <>
          {/* Filters */}
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <div className="relative flex-1 min-w-[200px] max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar por nome..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-md text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-black focus:border-black"
              />
              {search && (
                <button onClick={() => setSearch('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>

            {units.length > 0 && (
              <select
                value={unitFilter}
                onChange={e => setUnitFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-black focus:border-black cursor-pointer min-w-[160px]"
              >
                <option value="">Todas as unidades</option>
                {units.map(u => (
                  <option key={u.id} value={u.id}>
                    {u.block ? `${u.block.name} / ${u.number}` : u.number}
                  </option>
                ))}
              </select>
            )}

            <div className="flex gap-1.5">
              {[
                { key: 'all', label: 'Todos' },
                { key: 'inside', label: 'Dentro' },
                { key: 'awaiting_entry', label: 'Aguardando' },
                { key: 'expired', label: 'Não entrou' },
                { key: 'exited', label: 'Saiu' },
              ].map(({ key, label }) => (
                <button
                  key={key}
                  onClick={() => setStatusFilter(key)}
                  className={cn(
                    'px-3 py-1.5 rounded-full text-xs font-medium border transition-colors',
                    statusFilter === key
                      ? 'bg-black text-white border-black'
                      : 'bg-white border-gray-200 text-gray-500 hover:border-gray-400 hover:text-gray-700'
                  )}
                >
                  {label}
                </button>
              ))}
            </div>

            <div className="flex gap-1.5">
              {[
                { key: '' as const, label: 'Todos' },
                { key: 'authorized' as const, label: 'Autorizado' },
                { key: 'pending' as const, label: 'Pendente' },
              ].map(({ key, label }) => (
                <button
                  key={key}
                  onClick={() => setAuthStatusFilter(key)}
                  className={cn(
                    'px-3 py-1.5 rounded-full text-xs font-medium border transition-colors',
                    authStatusFilter === key
                      ? 'bg-black text-white border-black'
                      : 'bg-white border-gray-200 text-gray-500 hover:border-gray-400 hover:text-gray-700'
                  )}
                >
                  {label}
                </button>
              ))}
            </div>

            {serviceTypes.length > 0 && (
              <select
                value={serviceTypeFilter}
                onChange={e => setServiceTypeFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-black focus:border-black cursor-pointer min-w-[160px]"
              >
                <option value="">Tipo de serviço</option>
                {serviceTypes.map(t => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            )}

            {(unitFilter || statusFilter !== 'all' || search || serviceTypeFilter || authStatusFilter) && (
              <button
                onClick={() => { setUnitFilter(''); setStatusFilter('all'); setSearch(''); setServiceTypeFilter(''); setAuthStatusFilter(''); }}
                className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1"
              >
                <X className="h-3 w-3" /> Limpar
              </button>
            )}

            <button
              onClick={fetchServiceProviders}
              disabled={isLoading}
              className="ml-auto p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
              title="Atualizar"
            >
              <RefreshCw className={cn('h-4 w-4', isLoading && 'animate-spin')} />
            </button>
          </div>

          {/* Stats strip */}
          {items.length > 0 && !isLoading && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
              {[
                { label: 'Total', value: filteredItems.length, icon: Users, color: 'text-gray-900', bg: 'bg-gray-100' },
                { label: 'Dentro', value: filteredItems.filter(i => i.status_today === 'inside').length, icon: CheckCircle2, color: 'text-green-700', bg: 'bg-green-100' },
                { label: 'Aguardando', value: filteredItems.filter(i => i.status_today === 'awaiting_entry').length, icon: Clock, color: 'text-amber-700', bg: 'bg-amber-100' },
                { label: 'Saíram', value: filteredItems.filter(i => i.status_today === 'exited').length, icon: Shield, color: 'text-gray-500', bg: 'bg-gray-100' },
              ].map(({ label, value, icon: Icon, color, bg }) => (
                <div key={label} className="bg-white rounded-xl border border-gray-200 px-4 py-3 shadow-sm flex items-center gap-3">
                  <div className={cn('h-9 w-9 rounded-xl flex items-center justify-center flex-shrink-0', bg)}>
                    <Icon className={cn('h-4 w-4', color)} />
                  </div>
                  <div>
                    <p className="text-[10px] text-gray-400 uppercase tracking-widest font-medium">{label}</p>
                    <p className={cn('text-xl font-bold tabular-nums leading-tight', color)}>{value}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Cards */}
          {isLoading && items.length === 0 ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black" />
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="text-center py-12">
              <Briefcase className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">
                {items.length === 0 ? 'Nenhum prestador cadastrado' : 'Nenhum resultado'}
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                {items.length === 0 ? 'Comece adicionando um novo prestador.' : 'Tente ajustar os filtros.'}
              </p>
            </div>
          ) : (
            <div className="space-y-2 flex-1">
              {filteredItems.map(item => {
                const noWindowRestriction = item.role_metadata?.no_window_restriction === true;
                const windowStart = item.role_metadata?.entry_window_start ?? '—';
                const windowEnd = item.role_metadata?.entry_window_end ?? '—';
                const allowedDays = parseAllowedDays(item.role_metadata);
                const validUntil = item.valid_until
                  ? new Date(item.valid_until).toLocaleDateString('pt-BR')
                  : 'Indefinida';

                const statusBorderMap: Record<string, string> = {
                  inside: 'border-l-green-500',
                  awaiting_entry: 'border-l-amber-400',
                  expired: 'border-l-red-400',
                  exited: 'border-l-gray-300',
                };
                const leftBorder = statusBorderMap[item.status_today] ?? 'border-l-gray-200';

                return (
                  <div
                    key={item.role_id}
                    className={cn(
                      'bg-white rounded-xl border border-gray-200 border-l-4 shadow-sm hover:shadow-md transition-all cursor-pointer',
                      leftBorder
                    )}
                    onClick={() => openPersonDetail(item.person_id)}
                  >
                    <div className="flex items-center gap-4 px-4 py-3">
                      {/* Avatar */}
                      <div className="flex-shrink-0">
                        {item.photo ? (
                          <img
                            src={makeAbsolutePhotoUrl(item.photo) || item.photo}
                            alt={item.name}
                            className="h-10 w-8 rounded-md object-cover ring-2 ring-gray-100"
                          />
                        ) : (
                          <div className="h-10 w-8 rounded-md bg-gray-100 flex items-center justify-center">
                            <User className="h-5 w-5 text-gray-400" />
                          </div>
                        )}
                      </div>

                      {/* Name + company */}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-900 truncate">{item.name}</p>
                        {item.role_metadata?.contractor_name ? (
                          <p className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                            <Building2 className="h-3 w-3 flex-shrink-0" />
                            {item.role_metadata.contractor_name}
                          </p>
                        ) : (
                          <p className="text-xs text-gray-400 mt-0.5">Sem empresa</p>
                        )}
                      </div>

                      {/* Window + Days */}
                      <div className="hidden sm:block text-center min-w-[96px]">
                        <p className="text-[10px] text-gray-400 uppercase tracking-widest font-medium mb-0.5">Janela</p>
                        <p className="text-xs font-mono font-semibold text-gray-700">
                          {noWindowRestriction ? 'Sempre' : windowStart !== '—' ? `${windowStart}–${windowEnd}` : '—'}
                        </p>
                        <p className="text-[10px] text-gray-400 mt-0.5">{noWindowRestriction ? 'Todos os dias' : formatDays(allowedDays)}</p>
                      </div>

                      {/* Status */}
                      <div className="flex-shrink-0">
                        <StatusBadge status={item.status_today} />
                      </div>

                      {/* Last entry */}
                      <div className="hidden md:block text-right min-w-[80px]">
                        <p className="text-[10px] text-gray-400 uppercase tracking-widest font-medium mb-0.5">Último acesso</p>
                        <p
                          className="text-xs font-medium text-gray-600"
                          title={item.last_entry_at ? new Date(item.last_entry_at).toLocaleString('pt-BR') : undefined}
                        >
                          {formatRelativeTime(item.last_entry_at)}
                        </p>
                      </div>

                      {/* Valid until */}
                      <div className="hidden lg:block text-right min-w-[80px]">
                        <p className="text-[10px] text-gray-400 uppercase tracking-widest font-medium mb-0.5">Validade</p>
                        <p className="text-xs font-medium text-gray-600">{validUntil}</p>
                      </div>

                      {/* Edit button */}
                      <div className="flex-shrink-0" onClick={e => e.stopPropagation()}>
                        <button
                          onClick={() => setWindowEditState({
                            personId: item.person_id,
                            name: item.name,
                            windowStart: item.role_metadata?.entry_window_start ?? '07:00',
                            windowEnd: item.role_metadata?.entry_window_end ?? '09:00',
                            allowedDays,
                            contractorId: item.role_metadata?.contractor_id,
                            contractorName: item.role_metadata?.contractor_name,
                          })}
                          className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                          title="Editar janela de acesso"
                        >
                          <Clock className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* ── SCHEDULE VIEW ── */}
      {view === 'schedule' && (
        <div className="flex-1 overflow-auto">
          <ScheduleView items={items} onOpenPerson={openPersonDetail} />
        </div>
      )}

      {/* ── CONTRACTORS VIEW ── */}
      {view === 'contractors' && (
        <ContractorsView customerId={customerId} />
      )}

      {windowEditState && (
        <WindowEditModal
          state={windowEditState}
          customerId={customerId}
          onClose={() => setWindowEditState(null)}
          onSave={handleWindowSave}
        />
      )}

      {customerId && (
        <VisitorRegistrationModal
          open={showRegModal}
          customerId={customerId}
          personType="service_provider"
          selectedCustomer={selectedCustomer ? { type: selectedCustomer.type, name: selectedCustomer.name, condoType: (selectedCustomer as any).condoType, mobileAppEnabled: !!((selectedCustomer as any)?.condo_data?.mobile_app_enabled || (selectedCustomer as any)?.empresa_data?.mobile_app_enabled) } : undefined}
          onClose={() => setShowRegModal(false)}
          onSuccess={() => {
            setShowRegModal(false);
            fetchServiceProviders();
          }}
          onPreRegistrationCreated={() => {
            setShowRegModal(false);
            fetchServiceProviders();
          }}
        />
      )}
    </div>
  );
}
